/**
 * Coaching / Rotations access predicates over the caller's JWT claims
 * (the `caller` block of `/get-user`). The nav, the route guards and the
 * pages all read these so they agree with each other — and with the
 * Lambdas, which enforce the same rules from the same claims
 * (api/lib/admin-authority):
 *
 *   - Coaching: SystemAdmin or ProgramAdmin.
 *   - Rotations: SystemAdmin, ProgramAdmin, or RotationDirector; a
 *     director sees only their own rotations and may only add / remove
 *     members.
 *
 * A missing authority (no session yet) reads as no access.
 */
import { Role } from "./roles";
import type { CallerClaims } from "./roles";

export type AdminAuthority = {
  isSystemAdmin: boolean;
  /** Programs the caller is ProgramAdmin of. */
  adminProgramIds: string[];
  /** Rotations (person_group ids) the caller directs. */
  directedRotationIds: string[];
};

export const adminAuthorityFromCaller = (
  caller: CallerClaims,
): AdminAuthority => ({
  isSystemAdmin: (caller.roles ?? []).includes(Role.SystemAdmin),
  adminProgramIds: caller.scopedRoles?.[Role.ProgramAdmin]?.program ?? [],
  directedRotationIds:
    caller.scopedRoles?.RotationDirector?.person_group ?? [],
});

export const administersAnyProgram = (
  authority: AdminAuthority | null | undefined,
): boolean =>
  !!authority &&
  (authority.isSystemAdmin || authority.adminProgramIds.length > 0);

export const administersProgram = (
  authority: AdminAuthority | null | undefined,
  programId: string | null | undefined,
): boolean =>
  !!authority &&
  !!programId &&
  (authority.isSystemAdmin || authority.adminProgramIds.includes(programId));

export const canUseCoaching = administersAnyProgram;

export const canUseRotations = (
  authority: AdminAuthority | null | undefined,
): boolean =>
  administersAnyProgram(authority) ||
  (!!authority && authority.directedRotationIds.length > 0);

/** Directs rotations but administers no program — the limited Rotations view. */
export const isRotationDirectorOnly = (
  authority: AdminAuthority | null | undefined,
): boolean =>
  !administersAnyProgram(authority) &&
  !!authority &&
  authority.directedRotationIds.length > 0;
